import { type BadgeDef, newlyEarnedBadges } from "./badges";
import { applyEventToQuest, type QuestDef } from "./quests";
import { computeStreak, dayKeyUTC } from "./streaks";
import { DEFAULT_CURVE, levelForXp, type XpCurve } from "./xp";
import type { Awards, DayKey, GameEvent, PlayerState, StreakState } from "./types";

/**
 * Event-driven gamification engine. It is a pure REDUCER:
 *   (state, event[]) → (new state, rewards)
 * Domain-agnostic: the rules (XP per event, streak lines, badges,
 * quests) come through CONFIG. Each app defines them; the engine does not change.
 */

/** How much XP an event grants: a fixed number or a function of the event. */
export type XpRule = number | ((event: GameEvent) => number);

export interface GamificationConfig {
  /** eventType → XP. Unlisted types give 0. */
  xp: Record<string, XpRule>;
  /**
   * eventType → streak line(s) that this event "activates" (e.g.
   * "lesson.completed" → ["study"]). An event marks its `day` as active.
   */
  streakLines?: Record<string, string[]>;
  /** eventType → counter(s) to increment (default: the type itself). */
  counters?: Record<string, string[]>;
  badges?: BadgeDef[];
  quests?: QuestDef[];
  curve?: XpCurve;
}

export function initialState(freezes = 0): PlayerState {
  return {
    xp: 0,
    level: 1,
    counters: {},
    streaks: {},
    activeDays: {},
    badges: [],
    quests: {},
    freezes,
  };
}

function xpFor(rule: XpRule | undefined, event: GameEvent): number {
  if (rule == null) {
    return 0;
  }
  return typeof rule === "function" ? rule(event) : rule;
}

function dayOf(event: GameEvent): DayKey {
  return event.day ?? dayKeyUTC(event.at);
}

/**
 * Processes a batch of events over a state. Returns the new state and the
 * aggregated awards. It does NOT mutate the input state (defensive copy).
 */
export function applyEvents(
  state: PlayerState,
  events: readonly GameEvent[],
  config: GamificationConfig,
): { state: PlayerState; awards: Awards } {
  const curve = config.curve ?? DEFAULT_CURVE;
  const next: PlayerState = {
    ...state,
    counters: { ...state.counters },
    streaks: { ...state.streaks },
    activeDays: { ...state.activeDays },
    badges: [...state.badges],
    quests: { ...state.quests },
  };

  const fromLevel = state.level;
  let gainedXp = 0;
  const touchedLines = new Set<string>();

  for (const event of events) {
    // XP.
    const gained = xpFor(config.xp[event.type], event);
    gainedXp += gained;

    // Counters.
    const counterKeys = config.counters?.[event.type] ?? [event.type];
    for (const key of counterKeys) {
      next.counters[key] = (next.counters[key] ?? 0) + 1;
    }

    // Active days by streak line.
    const lines = config.streakLines?.[event.type] ?? [];
    const day = dayOf(event);
    for (const line of lines) {
      const arr = next.activeDays[line] ? [...next.activeDays[line]] : [];
      if (!arr.includes(day)) {
        arr.push(day);
      }
      next.activeDays[line] = arr;
      touchedLines.add(line);
    }

    // Quests.
    for (const quest of config.quests ?? []) {
      const prog = applyEventToQuest(quest, next.quests[quest.id], event, day);
      next.quests[quest.id] = prog;
    }
  }

  next.xp = state.xp + gainedXp;

  // Recomputes streaks for the touched lines (anchored to the most recent day seen).
  const streakAwards: Record<string, StreakState> = {};
  for (const line of touchedLines) {
    const days = next.activeDays[line] ?? [];
    const today = days.reduce((max, d) => (d > max ? d : max), days[0] ?? "");
    const streak = computeStreak(days, today, { freezes: next.freezes });
    streak.lastDay = today;
    next.streaks[line] = streak;
    streakAwards[line] = streak;
  }

  // Newly completed quests → bonus XP (once only; marked by `awarded`).
  const questsCompleted: string[] = [];
  for (const quest of config.quests ?? []) {
    const prog = next.quests[quest.id];
    const was = state.quests[quest.id]?.completed ?? false;
    if (prog?.completed && !was) {
      questsCompleted.push(quest.id);
      if (quest.bonusXp) {
        next.xp += quest.bonusXp;
      }
    }
  }

  // Level after total XP.
  next.level = levelForXp(next.xp, curve);

  // Badges (evaluated against the already-updated state).
  const earned = newlyEarnedBadges(next, config.badges ?? []);
  for (const badge of earned) {
    next.badges.push(badge.id);
    if (badge.bonusXp) {
      next.xp += badge.bonusXp;
    }
  }
  // A badge with a bonus can raise the level again.
  next.level = levelForXp(next.xp, curve);

  const awards: Awards = {
    xp: next.xp - state.xp,
    badges: earned.map((b) => b.id),
    questsCompleted,
    streaks: streakAwards,
  };
  if (next.level > fromLevel) {
    awards.leveledUp = { from: fromLevel, to: next.level };
  }
  return { state: next, awards };
}
